import {
  type Bdkv2Expression,
  type Bdkv2ConceptDescriptor,
  type V2BookTriggerDescriptor,
  Bdkv2BinaryOperator,
  Bdkv2ConceptScalarType,
} from '@/generated/hooks/bdk/models';
import {
  type TExtractedCondition,
  createBdkValue,
  createBinaryExpression,
  createUnaryNotExpression,
  combineWithAnd,
  extractConditions,
} from '../TriggerTypeSelector/utils';
import { unwrapInnerType } from './utils';

/**
 * Operators exposed by the generic trigger filter builder (v1, conservative).
 *
 * These map onto the BDK expression operators on serialization (see
 * OPERATOR_SPECS). Numeric/date comparisons are intentionally omitted for v1.
 */
export type TFilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains';

/**
 * A single filter condition row in the builder UI.
 */
export interface IFilterCondition {
  /** Client-only stable key for React lists and edits */
  id: string;
  /** Top-level event field name (matches an `event_type` key) */
  field: string;
  /** The comparison operator */
  operator: TFilterOperator;
  /** The comparison value */
  value?: string | number | boolean;
}

/**
 * A filterable event field paired with its concept descriptor, used to drive
 * the field dropdown, operator options, and per-type value input.
 *
 * Top-level scalar/enum fields have `groupLabel: null`. For dictionary fields
 * one level deep (e.g. an `email` dictionary), each sub-field is emitted as a
 * separate entry with `name: "email.subject"`, `label: "subject"`, and
 * `groupLabel: "email"` so the dropdown can render a labelled group.
 */
export interface IFilterableField {
  /** Dotted field path used in the filter expression (e.g. "subject" or "email.subject") */
  name: string;
  /** Human-readable label shown in the dropdown item (the leaf key, e.g. "subject") */
  label: string;
  /** Parent key for nested fields (e.g. "email"); null for top-level fields */
  groupLabel: string | null;
  /** Concept descriptor describing the field's type */
  descriptor: Bdkv2ConceptDescriptor;
}

/**
 * Coarse filter category derived from a field's concept type. Used to pick the
 * available operators. `null` means the field is not filterable in v1.
 */
type TFilterFieldKind = 'text' | 'number' | 'boolean' | 'enum';

/**
 * How a `TFilterOperator` serializes into a BDK expression leaf.
 */
interface IOperatorSpec {
  kind: 'binary' | 'notBinary';
  binaryOperator: Bdkv2BinaryOperator;
  /** Whether the operator needs a value to be considered complete */
  requiresValue: boolean;
}

const OPERATOR_SPECS: Record<TFilterOperator, IOperatorSpec> = {
  equals: {
    kind: 'binary',
    binaryOperator: Bdkv2BinaryOperator.BinaryOperatorEquals,
    requiresValue: true,
  },
  notEquals: {
    kind: 'binary',
    binaryOperator: Bdkv2BinaryOperator.BinaryOperatorNotEquals,
    requiresValue: true,
  },
  contains: {
    kind: 'binary',
    binaryOperator: Bdkv2BinaryOperator.BinaryOperatorHas,
    requiresValue: true,
  },
  notContains: {
    kind: 'notBinary',
    binaryOperator: Bdkv2BinaryOperator.BinaryOperatorHas,
    requiresValue: true,
  },
};

/** Operator options offered per field kind. */
const OPERATORS_BY_KIND: Record<TFilterFieldKind, TFilterOperator[]> = {
  text: ['equals', 'notEquals', 'contains', 'notContains'],
  number: ['equals', 'notEquals'],
  // Boolean fields only make sense with "equals"; the operator dropdown is hidden
  // entirely in the UI — this list exists for getOperatorsForDescriptor callers.
  boolean: ['equals'],
  enum: ['equals', 'notEquals'],
};

/** Default operator set for fields that fall through to text-like handling. */
const DEFAULT_OPERATORS = OPERATORS_BY_KIND.text;

/**
 * Classifies a concept descriptor into a filterable kind, or `null` if the
 * field cannot be filtered in v1.
 *
 * v1 is conservative: only top-level Text/Number/Boolean/UUID/Conceptual
 * scalars and enums are filterable. Date/Time/File scalars and complex types
 * (dictionary/list/table/sensitive/etc.) are excluded — they need custom value
 * inputs and/or backend confirmation.
 */
function getFilterFieldKind(
  descriptor: Bdkv2ConceptDescriptor,
): TFilterFieldKind | null {
  const conceptType = unwrapInnerType(descriptor);
  if (!conceptType) return null;

  if (conceptType.enum_type) return 'enum';

  switch (conceptType.scalar_type) {
    case Bdkv2ConceptScalarType.ConceptScalarTypeText:
    case Bdkv2ConceptScalarType.ConceptScalarTypeUUID:
    case Bdkv2ConceptScalarType.ConceptScalarTypeConceptual:
      return 'text';
    case Bdkv2ConceptScalarType.ConceptScalarTypeNumber:
      return 'number';
    case Bdkv2ConceptScalarType.ConceptScalarTypeBoolean:
      return 'boolean';
    default:
      // Date/Datetime/Time/File scalars and all non-scalar/complex types are
      // not filterable in v1.
      return null;
  }
}

/**
 * Returns the operators that should be offered for a field, derived from its
 * concept type.
 */
export function getOperatorsForDescriptor(
  descriptor: Bdkv2ConceptDescriptor,
): TFilterOperator[] {
  const kind = getFilterFieldKind(descriptor);
  return kind ? OPERATORS_BY_KIND[kind] : DEFAULT_OPERATORS;
}

/**
 * Maps a client-tool operator string (as shifu sends it, e.g. `"equals"`,
 * `"not equals"`, `"not-contains"`) to a builder `TFilterOperator`.
 *
 * Case and separators are normalized so `"not equals"`, `"not-equals"`, and
 * `"not_equals"` all resolve. Returns `undefined` for anything unrecognized so
 * callers can surface an error to shifu rather than silently defaulting to
 * `equals`.
 *
 * @param operator - Raw operator string from the client tool
 * @returns The matching builder operator, or `undefined` if unknown
 */
export function parseFilterOperator(
  operator: string,
): TFilterOperator | undefined {
  const normalized = operator
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const map: Record<string, TFilterOperator> = {
    equals: 'equals',
    not_equals: 'notEquals',
    contains: 'contains',
    not_contains: 'notContains',
  };
  return map[normalized];
}

/**
 * Returns whether the given operator requires a value to be set.
 * All current operators require a value.
 * Keeping for forward compatibility in case we add value-less operators like "exists" in the future.
 */
export function operatorRequiresValue(operator: TFilterOperator): boolean {
  return OPERATOR_SPECS[operator].requiresValue;
}

/**
 * Returns true when the descriptor describes a boolean field (after unwrapping
 * optional). Used to adjust filter row UX for boolean fields — the operator
 * dropdown is hidden since it's always "equals".
 */
export function isBooleanDescriptor(
  descriptor: Bdkv2ConceptDescriptor,
): boolean {
  return (
    unwrapInnerType(descriptor)?.scalar_type ===
    Bdkv2ConceptScalarType.ConceptScalarTypeBoolean
  );
}

/**
 * Returns the appropriate initial value for a new condition on this field.
 *
 * Boolean fields default to `false` so the Switch renders in a defined state
 * and the "required value" validation never fires on first submit.
 * All other field types start with `undefined`.
 */
export function getDefaultValueForDescriptor(
  descriptor: Bdkv2ConceptDescriptor,
): string | number | boolean | undefined {
  return isBooleanDescriptor(descriptor) ? false : undefined;
}

/**
 * Extracts filterable event fields from a trigger's event schema.
 *
 * Unlike `extractTriggerFieldGroups` (which explodes dictionaries into
 * `eventKey.fieldKey` options for input mapping), this keeps the concept
 * descriptor so the UI can derive operators and render the right value input.
 *
 * Supports one level of nesting: a top-level dictionary field (e.g. `email`)
 * is not emitted itself but its scalar/enum sub-fields are emitted as
 * `{ name: "email.subject", label: "subject", groupLabel: "email" }` entries
 * so the dropdown can render a labelled group. Deeper nesting is not
 * traversed.
 *
 * Non-filterable scalar types (Date/Datetime/Time/File) and complex types
 * (list/table/sensitive) are excluded at every level.
 *
 * @param trigger - The selected trigger descriptor
 * @returns Filterable fields with their descriptors
 */
export function extractFilterableFields(
  trigger?: V2BookTriggerDescriptor,
): IFilterableField[] {
  if (!trigger?.event_type) {
    return [];
  }

  const fields: IFilterableField[] = [];

  for (const [key, descriptor] of Object.entries(trigger.event_type)) {
    if (!descriptor) continue;

    const conceptType = unwrapInnerType(descriptor);

    if (conceptType?.dictionary_type?.fields) {
      // Dictionary field: traverse one level into its sub-fields.
      for (const field of conceptType.dictionary_type.fields) {
        if (!field.key || !field.value) continue;
        // Construct a minimal descriptor from the sub-field's type so
        // getFilterFieldKind, getOperatorsForDescriptor, and ConfigInput can
        // treat it the same way as a top-level descriptor.
        const subDescriptor: Bdkv2ConceptDescriptor = {
          type: field.value,
          description: field.description,
        };
        if (getFilterFieldKind(subDescriptor) === null) continue;
        fields.push({
          name: `${key}.${field.key}`,
          label: field.key,
          groupLabel: key,
          descriptor: subDescriptor,
        });
      }
    } else {
      // Top-level scalar / enum field.
      if (getFilterFieldKind(descriptor) === null) continue;
      fields.push({ name: key, label: key, groupLabel: null, descriptor });
    }
  }

  return fields;
}

/**
 * Returns whether a condition value is "missing" for serialization purposes.
 * `false` and `0` are valid user-entered values and are NOT missing.
 */
function isValueMissing(value: IFilterCondition['value']): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Returns the ids of conditions that are missing a required value.
 *
 * A condition is invalid when its operator requires a value and the value is empty. Used to surface inline validation errors
 * before saving rather than silently dropping the condition.
 */
export function getInvalidFilterConditionIds(
  conditions: IFilterCondition[],
): string[] {
  return conditions
    .filter(
      condition =>
        condition.field &&
        operatorRequiresValue(condition.operator) &&
        isValueMissing(condition.value),
    )
    .map(condition => condition.id);
}

/**
 * Builds a single BDK expression leaf for a condition, or `undefined` if the
 * condition is incomplete (no field, or a required value is missing).
 */
function conditionToLeaf(
  condition: IFilterCondition,
): Bdkv2Expression | undefined {
  if (!condition.field) return undefined;

  const spec = OPERATOR_SPECS[condition.operator];
  if (!spec) return undefined;

  if (spec.requiresValue && isValueMissing(condition.value)) {
    return undefined;
  }

  const binary = createBinaryExpression(
    condition.field,
    spec.binaryOperator,
    createBdkValue(condition.value as string | number | boolean),
  );

  return spec.kind === 'notBinary' ? createUnaryNotExpression(binary) : binary;
}

/**
 * Converts a list of filter conditions into a BDK filter expression.
 *
 * Incomplete conditions are dropped. Multiple conditions are combined with AND
 * (left-folded), matching the email converter's shape so both produce
 * identical expression trees.
 *
 * @param conditions - Filter conditions from the builder
 * @returns Filter expression, or `undefined` when there are no usable conditions
 */
export function convertConditionsToExpression(
  conditions: IFilterCondition[],
): Bdkv2Expression | undefined {
  const leaves = conditions
    .map(conditionToLeaf)
    .filter((leaf): leaf is Bdkv2Expression => leaf !== undefined);

  if (leaves.length === 0) return undefined;
  if (leaves.length === 1) return leaves[0];

  let combined = leaves[0];
  for (let i = 1; i < leaves.length; i++) {
    combined = combineWithAnd(combined, leaves[i]);
  }
  return combined;
}

/**
 * Maps an extracted condition back to a builder operator, or `null` when the
 * operator cannot be represented in the current UI.
 *
 * `UnaryOperatorExists` conditions (no `operator` field from `extractConditions`)
 * are dropped — they may appear in expressions authored by older code or
 * external tools but are no longer part of the filter UI.
 */
function toFilterOperator(
  condition: TExtractedCondition,
): TFilterOperator | null {
  if (condition.operator === undefined) {
    return null; // UnaryOperatorExists — not supported in current UI
  }
  switch (condition.operator) {
    case Bdkv2BinaryOperator.BinaryOperatorEquals:
      return 'equals';
    case Bdkv2BinaryOperator.BinaryOperatorNotEquals:
      return 'notEquals';
    case Bdkv2BinaryOperator.BinaryOperatorHas:
      return condition.isNegated ? 'notContains' : 'contains';
    default:
      return null; // unrecognized operator — drop rather than corrupt
  }
}

/**
 * Converts a BDK filter expression back into builder conditions.
 *
 * Reuses the shared recursive `extractConditions`, then maps each flattened
 * condition to a builder operator and assigns a fresh client id. Conditions
 * with operators not supported by the current UI (e.g. legacy `UnaryOperatorExists`)
 * are dropped silently.
 *
 * @param expression - Filter expression from the trigger instance
 * @returns Filter conditions
 */
export function convertExpressionToConditions(
  expression: Bdkv2Expression | undefined,
): IFilterCondition[] {
  if (!expression) return [];

  const extracted: TExtractedCondition[] = [];
  extractConditions(expression, extracted);

  return extracted.flatMap(condition => {
    const operator = toFilterOperator(condition);
    if (operator === null) return [];
    return [
      {
        id: crypto.randomUUID(),
        field: condition.field,
        operator,
        value: condition.value as string | number | boolean | undefined,
      },
    ];
  });
}
